/**
 * Минимальный typed API-клиент.
 * Типы генерируются командой: pnpm gen:api (из openapi.json FastAPI).
 * До генерации типов — базовый fetch-wrapper.
 *
 * На 401 автоматически пробует /api/auth/refresh и повторяет запрос один раз.
 * Для refresh-эндпоинта retry не делается (избегаем бесконечной рекурсии).
 */

const BASE = import.meta.env.VITE_API_BASE_URL ?? ''

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(`API ${status}: ${detail}`)
  }
}

let _refreshing: Promise<boolean> | null = null

async function _doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
    return res.ok
  } catch {
    return false
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  _retry = true,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',          // передаём httpOnly cookies (refresh token)
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  // Автоматический silent refresh при 401 (один раз, не на сам /refresh)
  // Исключаем только /api/auth/refresh — иначе /api/auth/me тоже не обновится.
  if (res.status === 401 && _retry && path !== '/api/auth/refresh') {
    if (!_refreshing) _refreshing = _doRefresh().finally(() => { _refreshing = null })
    const ok = await _refreshing
    if (ok) return request<T>(method, path, body, false)
  }

  if (!res.ok) {
    let detail = res.statusText
    try {
      const json = await res.json()
      detail = json.detail ?? detail
    } catch { /* noop */ }
    throw new ApiError(res.status, detail)
  }

  // 204 No Content
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get:    <T>(path: string)               => request<T>('GET',    path),
  post:   <T>(path: string, body: unknown) => request<T>('POST',   path, body),
  patch:  <T>(path: string, body: unknown) => request<T>('PATCH',  path, body),
  delete: <T>(path: string)               => request<T>('DELETE', path),
}

// ── Health ───────────────────────────────────────────────────────────────────
export interface HealthResponse {
  status: string
  env: string
  version: string
}

export const healthApi = {
  check: () => api.get<HealthResponse>('/health'),
}
